import AsyncSelect from '@/components/Form/Shared/AsyncSelect'
import CloseIcons from '@/icons/CloseIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import EditIcons from '@/icons/EditIcons'
import MoreVertIcons from '@/icons/MoreVert'
import RedoIcons from '@/icons/RedoIcons'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import { formatDateWithFormatter, uniqBy } from '@/utils/function'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, IconButton, Typogaphy } from 'vnetwork-ui'
import { getExpectedDateStatus } from './helpers'
import { JourneyHeaderProps } from './types'
import { useCustomerReadOnly } from '../../CustomerReadOnlyContext'

const JourneyHeader = ({
    journey,
    journeyIndex,
    isUpsell,
    outcome,
    isCompleted,
    isLocked,
    showJourneyInfo,
    onToggleJourneyInfo,
    onReEngage,
    onMarkAsLost,
    onDeleteJourney,
    onToggleExpand,
    formControl,
}: JourneyHeaderProps) => {
    const { t } = useTranslation()
    const isReadOnly = useCustomerReadOnly()
    const [showMenu, setShowMenu] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)

    const handleMarkAsLost = () => {
        setShowMenu(false)
        onMarkAsLost()
    }

    const handleDeleteJourney = () => {
        setShowMenu(false)
        onDeleteJourney()
    }

    return (
        <div
            className={`px-4 py-3 bg-gray-50 space-y-2 ${
                !isCompleted ? 'border-b border-gray-100' : ''
            } ${isCompleted ? 'cursor-pointer hover:bg-gray-100' : ''}`}
            onClick={() => isCompleted && onToggleExpand()}
            data-tour="journey-header"
        >
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    {/* Expand/Collapse Icon for completed journeys */}
                    {isCompleted && (
                        <svg
                            className="w-4 h-4 text-gray-400 rotate-90 flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                    )}
                    <IconButton className="bg-gray-200 text-gray-500 font-medium text-sm">
                        {journeyIndex + 1}
                    </IconButton>
                    {/* Badges */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        {isUpsell && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-600 font-medium">
                                Upsell
                            </span>
                        )}
                        {outcome === 'won' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 font-medium">
                                Won
                            </span>
                        )}
                        {outcome === 'lost' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 font-medium">
                                On Hold
                            </span>
                        )}
                        {outcome === 'terminated' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-500 font-medium">
                                Ended
                            </span>
                        )}
                    </div>

                    {journey?.employee_sale_followers &&
                        journey.employee_sale_followers.length > 0 && (
                            <div
                                className="flex items-center gap-1 min-w-0 flex-shrink"
                                data-tour="sales-followers"
                            >
                                <span className="text-xs text-gray-500">
                                    Sales:
                                </span>
                                <span
                                    className="text-xs font-medium truncate max-w-[200px]"
                                    title={uniqBy(
                                        journey?.employee_sale_followers,
                                        'employee_id'
                                    )
                                        ?.map((e) => e.employee_name)
                                        .join(', ')}
                                >
                                    {uniqBy(
                                        journey?.employee_sale_followers,
                                        'employee_id'
                                    )
                                        ?.map((e) => e.employee_name)
                                        .slice(0, 2)
                                        .join(', ')}
                                    {uniqBy(
                                        journey?.employee_sale_followers,
                                        'employee_id'
                                    )?.length > 2 &&
                                        ` +${uniqBy(journey?.employee_sale_followers, 'employee_id')?.length - 2}`}
                                </span>
                            </div>
                        )}
                    <span className="text-xs text-gray-400 flex-shrink-0">
                        {formatDateWithFormatter(
                            journey.customer_journey_created_at,
                            'DD/MM/YYYY'
                        )}
                    </span>

                    {/* Expected Sale Date */}
                    {journey.customer_journey_explaination?.expected_date &&
                        !isCompleted &&
                        (() => {
                            const expectedStatus = getExpectedDateStatus(
                                journey.customer_journey_explaination
                                    .expected_date
                            )
                            return (
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <span className="text-gray-300">|</span>
                                    <div
                                        className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                            expectedStatus.isOverdue
                                                ? 'bg-red-100 text-red-700'
                                                : expectedStatus.isWarning
                                                  ? 'bg-amber-100 text-amber-700'
                                                  : 'bg-gray-100 text-gray-600'
                                        }`}
                                    >
                                        {expectedStatus.isOverdue && (
                                            <svg
                                                className="w-3 h-3"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                                />
                                            </svg>
                                        )}
                                        {expectedStatus.isWarning &&
                                            !expectedStatus.isOverdue && (
                                                <svg
                                                    className="w-3 h-3"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                    />
                                                </svg>
                                            )}
                                        <span>
                                            {expectedStatus.isOverdue
                                                ? t(
                                                      'sale-process.overdue-days',
                                                      {
                                                          days: expectedStatus.daysLeft,
                                                      }
                                                  )
                                                : expectedStatus.daysLeft === 0
                                                  ? t('due-today')
                                                  : expectedStatus.isWarning
                                                    ? t(
                                                          'sale-process.due-in-days',
                                                          {
                                                              days: expectedStatus.daysLeft,
                                                          }
                                                      )
                                                    : formatDateWithFormatter(
                                                          journey
                                                              .customer_journey_explaination
                                                              .expected_date,
                                                          'DD/MM/YYYY'
                                                      )}
                                        </span>
                                    </div>
                                </div>
                            )
                        })()}
                </div>

                {/* Actions */}
                <div
                    className="flex items-center gap-2 flex-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Edit Journey Info button - Only for active journeys */}
                    {!isLocked && !isCompleted && (
                        <button
                            className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
                                showJourneyInfo
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'text-gray-500 hover:bg-gray-100'
                            }`}
                            onClick={onToggleJourneyInfo}
                            data-tour="edit-journey-info-btn"
                        >
                            <EditIcons className="w-3 h-3" />
                            {t('sale-process.journey-information')}
                        </button>
                    )}

                    {/* Re-engage button for lost/terminated */}
                    {(outcome === 'lost' || outcome === 'terminated') &&
                        !isReadOnly && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={onReEngage}
                                className="text-xs border-gray-300 text-gray-600"
                            >
                                <RedoIcons className="w-3 h-3 mr-1" />
                                {t('re-engage')}
                            </Button>
                        )}

                    {/* Menu */}
                    {!isLocked && (
                        <div
                            className="relative"
                            ref={menuRef}
                            data-tour="journey-actions"
                        >
                            <IconButton
                                size="small"
                                onClick={() => setShowMenu(!showMenu)}
                            >
                                <MoreVertIcons className="w-4 h-4 text-gray-400" />
                            </IconButton>
                            {showMenu && (
                                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[140px] py-1">
                                    <button
                                        className="w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
                                        onClick={handleMarkAsLost}
                                    >
                                        <CloseIcons className="w-4 h-4" />
                                        {t('mark-as-on-hold')}
                                    </button>
                                    <div className="border-t border-gray-100 my-1" />
                                    <button
                                        className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                        onClick={handleDeleteJourney}
                                    >
                                        <DeleteOutlineIcons className="w-4 h-4" />
                                        {t('delete-journey')}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {/* Service Interestings */}
            <div
                className="flex-shrink-0"
                onClick={(e) => e.stopPropagation()}
                data-tour="service-interestings"
            >
                {!isLocked && !isCompleted ? (
                    <div className="w-full">
                        <AsyncSelect
                            asyncOptions={getProductsGroupOptions}
                            control={formControl.control}
                            name="service_interestings"
                            placeholder={t('select-services')}
                            multiple
                            size="small"
                        />
                    </div>
                ) : (
                    <Typogaphy className="text-sm font-medium text-gray-800">
                        {journey.customer_journey_explaination?.service_interestings
                            ?.slice(0, 3)
                            .join(', ') || t('untitled')}
                        {(journey.customer_journey_explaination
                            ?.service_interestings?.length ?? 0) > 3 &&
                            ` +${(journey.customer_journey_explaination?.service_interestings?.length ?? 0) - 3}`}
                    </Typogaphy>
                )}
            </div>
        </div>
    )
}

export default JourneyHeader
